import { requestWithToken as request } from "./api";
import { readStringFlag } from "../utils/localFlag";
import { createResponseMemo } from "../utils/responseMemo";

const BASE = "/financial-reports";

// Memo por pestaña de las respuestas GET del reporte (ver utils/responseMemo):
// clave = empresa activa + URL. Sólo guarda éxitos; api.js lo vacía tras
// cualquier escritura. `clearReportMemo` existe para tests y para forzar un
// re-fetch explícito (p.ej. botón "actualizar").
const reportMemo = createResponseMemo({ maxEntries: 60, ttlMs: 5 * 60 * 1000 });

export function clearReportMemo() {
    reportMemo.clear();
}

// Fuente de la última respuesta de red del reporte, según la cabecera
// X-Report-Source del backend: "live" | "fact" | "stale" | "cache".
// "stale" = la empresa está en modo precalculado y su tabla de hechos tiene un
// recálculo pendiente (etapa 2 del plan): la respuesta trae el dato anterior y
// NO se memoiza, así el próximo fetch ya sale al día. El banner de
// DashboardReports se entera vía `subscribeReportSource` (pub/sub plano: la
// capa api no importa el store ni reactividad).
const REPORT_SOURCE_HEADER = "x-report-source";
export const REPORT_SOURCE_STALE = "stale";
const sourceListeners = new Set();

export function subscribeReportSource(listener) {
    sourceListeners.add(listener);
    return () => sourceListeners.delete(listener);
}

// Sólo para tests: deja el pub/sub sin suscriptores.
export function resetReportSourceListeners() {
    sourceListeners.clear();
}

function readReportSource(response) {
    const headers = (response && response.headers) || {};
    return headers[REPORT_SOURCE_HEADER] || headers["X-Report-Source"] || null;
}

function memoGet(url, config = {}) {
    const key = `${readStringFlag("activeCompanyId") || ""}|${url}`;
    const cached = reportMemo.get(key);
    if (cached) return Promise.resolve(cached);
    return request.get(url, config).then((response) => {
        const source = readReportSource(response);
        if (source !== REPORT_SOURCE_STALE) reportMemo.set(key, response);
        sourceListeners.forEach((listener) => listener(source));
        return response;
    });
}

// Frescura de la tabla de hechos de la empresa activa ({ source, job_running }).
// Lo sondea el banner "Actualizando…": pausado mientras el reporte está al
// día y rápido hasta que `source` deje de ser "stale". `pull` pide al backend
// que además adelante el recálculo si encuentra la tabla desactualizada. A
// propósito SIN memo ni pub/sub: es el estado del momento y no debe
// re-encender el banner ni quedar cacheado.
export function getReportFactStatus({ pull = false } = {}) {
    return request.get(`${BASE}/fact-status/${buildQuery({ pull: pull ? 1 : null })}`);
}

function buildQuery(params) {
    const entries = Object.entries(params).filter(
        ([, v]) => v !== undefined && v !== null && v !== ""
    );
    if (entries.length === 0) return "";
    const qs = entries
        .map(([k, v]) => `${encodeURIComponent(k)}=${encodeURIComponent(v)}`)
        .join("&");
    return `?${qs}`;
}

// Generic endpoints — three resource-oriented calls cover every tab.
// `config` (opcional) se pasa al request de axios — mismo patrón que
// saveFintocUserHolder. Hoy lo usan los stores take-latest para viajar el
// `signal` de AbortController (inerte en axios 0.21; abort real en 1.x).
export function getCategorySummary(category, endDate, config = {}, initDate = null) {
    // `init_date` (opcional) sólo lo usan las cards de balance por nivel:
    // inicio del rango para la card "acumulado del período" (clasificador).
    const query = initDate
        ? { init_date: initDate, end_date: endDate }
        : { end_date: endDate };
    return memoGet(`${BASE}/${category}/summary/${buildQuery(query)}`, config);
}

export function getCategoryBreakdown(category, initDate, endDate, groupBy = "cuentas", period = "month", config = {}) {
    return memoGet(
        `${BASE}/${category}/breakdown/${buildQuery({
            init_date: initDate,
            end_date: endDate,
            group_by: groupBy,
            period,
        })}`,
        config
    );
}

export function getCategoryCharts(category, initDate, endDate, period = "month", topN = 7, groupBy = "clientes") {
    return memoGet(
        `${BASE}/${category}/charts/${buildQuery({
            init_date: initDate,
            end_date: endDate,
            period,
            top_n: topN,
            group_by: groupBy,
        })}`
    );
}

// `metric` sólo aplica a profitability (ventas | margen_*); buildQuery omite
// los null, así los demás tabs no lo mandan y el backend no lo reenvía a sus
// use cases.
export function getCategoryComparison(
    category, endDate, years, groupBy = null, item = null, metric = null
) {
    return memoGet(
        `${BASE}/${category}/comparison/${buildQuery({
            end_date: endDate,
            years: Array.isArray(years) ? years.join(",") : years,
            group_by: groupBy,
            item,
            metric,
        })}`
    );
}

// `detail` (opcional): "cuentas" abre cada línea del P&L en sus cuentas de
// nivel 3 (`accounts_detail` en la respuesta). null → contrato plano de siempre
// (buildQuery omite los null).
export function getResultStateByPeriod(period, initDate, endDate, detail = null) {
    return memoGet(
        `${BASE}/state/${buildQuery({ period, init_date: initDate, end_date: endDate, detail })}`
    );
}

// Backwards-compatible wrappers — kept so the 7 Vuex modules don't need to change.
// Each one is a one-line delegation to the generic call above.

export function getSalesByLevel3(initDate, endDate, groupBy, period) {
    return getCategoryBreakdown("sales", initDate, endDate, groupBy, period);
}

export function getSalesCharts(initDate, endDate, period, topN, groupBy) {
    return getCategoryCharts("sales", initDate, endDate, period, topN, groupBy);
}

export function getSalesComparison(endDate, years, groupBy, item) {
    return getCategoryComparison("sales", endDate, years, groupBy, item);
}

export function getExpensesByLevels(initDate, endDate, groupBy, period) {
    return getCategoryBreakdown("expenses", initDate, endDate, groupBy, period);
}

export function getExpensesCharts(initDate, endDate, period, topN, groupBy) {
    return getCategoryCharts("expenses", initDate, endDate, period, topN, groupBy);
}

export function getExpensesComparison(endDate, years, groupBy = null, item = null) {
    return getCategoryComparison("expenses", endDate, years, groupBy, item);
}

// Expenses analysis modal — drill-down de un mes de Gastos. Mismo contrato de
// query que getCostsAnalysis; el backend devuelve statement (con eficiencia) +
// counterparts_ranking.
// `accountGroup` (nivel 2 de `account`) desambigua cuentas homónimas: el
// backend filtra la cuenta dentro de ese grupo.
export function getExpensesAnalysis(month, account = null, groupBy = null, selectedItems = null, accountLevel = null, accountGroup = null, only = null) {
    return memoGet(
        `${BASE}/expenses/analysis/${buildQuery({
            month,
            account,
            account_level: accountLevel,
            account_group: accountGroup,
            group_by: groupBy,
            attribute_filters: serializeAttributeFilters(selectedItems),
            only,
        })}`
    );
}

export function getCostsByLevels(initDate, endDate, groupBy, period) {
    return getCategoryBreakdown("costs", initDate, endDate, groupBy, period);
}

export function getCostsCharts(initDate, endDate, period, topN, groupBy) {
    return getCategoryCharts("costs", initDate, endDate, period, topN, groupBy);
}

export function getCostsComparison(endDate, years, groupBy = null, item = null) {
    return getCategoryComparison("costs", endDate, years, groupBy, item);
}

export function getAssetsByLevels(initDate, endDate, groupBy, period, config) {
    return getCategoryBreakdown("assets", initDate, endDate, groupBy, period, config);
}

export function getAssetsSummaryCards(endDate, config, initDate = null) {
    return getCategorySummary("assets", endDate, config, initDate);
}

export function getLiabilitiesByLevels(initDate, endDate, groupBy, period, config) {
    return getCategoryBreakdown("liabilities", initDate, endDate, groupBy, period, config);
}

export function getLiabilitiesSummaryCards(endDate, config, initDate = null) {
    return getCategorySummary("liabilities", endDate, config, initDate);
}

export function getEquityByLevels(initDate, endDate, groupBy, period, config) {
    return getCategoryBreakdown("equity", initDate, endDate, groupBy, period, config);
}

export function getEquitySummaryCards(endDate, config, initDate = null) {
    return getCategorySummary("equity", endDate, config, initDate);
}

// Resumen de Balance: breakdown de los tres nivel-1 (Activos/Pasivos/Patrimonio)
// en una respuesta (`groups`). Siempre por cuentas — el Resumen no agrupa por
// contraparte/categoría, así que no expone `groupBy`.
export function getBalanceByLevels(initDate, endDate, period) {
    return getCategoryBreakdown("balance", initDate, endDate, "cuentas", period);
}

export function getNonOperationalByLevels(initDate, endDate, groupBy, period, config) {
    return getCategoryBreakdown("non-operational", initDate, endDate, groupBy, period, config);
}

export function getNonOperationalSummaryCards(endDate, config) {
    return getCategorySummary("non-operational", endDate, config);
}

// Comparación año-contra-año de la card "Evolución de Ventas & Márgenes":
// una línea por año para UNA métrica (ventas en pesos o un margen en %).
// {months, series: [{year, values}], available_years, metric, unit}.
export function getProfitabilityComparison(endDate, years, metric = "ventas") {
    return getCategoryComparison("profitability", endDate, years, null, null, metric);
}

// Sales analysis modal — drill-down de un mes específico.
// `selectedItems` es un dict { tipo: [name, ...] }; se envía JSON-encoded
// en el query param `attribute_filters`. El backend descarta entradas con
// listas vacías, así que filtramos client-side antes de serializar para
// no inflar la URL.
//
// `groupBy` acepta string (legacy: una sola categoría) o array (varias
// categorías para mostrar múltiples distribuciones en simultáneo). El
// backend splittea el query param por comas, así que serializamos así.
export function getSalesAnalysis(month, account = null, groupBy = null, selectedItems = null) {
    const groupByParam = Array.isArray(groupBy) ? groupBy.join(",") : groupBy;
    return memoGet(
        `${BASE}/sales/analysis/${buildQuery({
            month,
            account,
            group_by: groupByParam,
            attribute_filters: serializeAttributeFilters(selectedItems),
        })}`
    );
}

// Drawer "Análisis de Categorías" de Rentabilidad: rentabilidad de un rango
// abierta por ítem de una categoría (tiendas/marcas). `selectedItems` acota
// los ítems, con el mismo contrato de query que los otros análisis.
// {headline, cards: [{key, label, amount, percent, items}], evolution, items,
//  available_categories}.
// El rango y la granularidad (`period`: 'month' | 'quarter' | 'ytd' | 'year')
// son los del tab, resueltos con `resolveDateRange` (plan de filtros del
// drawer, 2026-10-05).
export function getProfitabilityAnalysis({
    initDate, endDate, period, category = null, selectedItems = null,
}) {
    return memoGet(
        `${BASE}/profitability/analysis/${buildQuery({
            init_date: initDate,
            end_date: endDate,
            period,
            group_by: category,
            attribute_filters: serializeAttributeFilters(selectedItems),
        })}`
    );
}

// Costs analysis modal — drill-down de un mes de Costos. Mismo contrato de
// query que getSalesAnalysis (month/account/group_by/attribute_filters); el
// backend devuelve un payload distinto (statement + counterparts_ranking).
// `only = "ranking"` recorta la respuesta al ranking de contrapartes: para las
// interacciones que lo refrescan sin mirar el estado, el backend se ahorra el
// mes anterior, el del año pasado y las ventas (4 lecturas → 1).
export function getCostsAnalysis(month, account = null, groupBy = null, selectedItems = null, accountLevel = null, accountGroup = null, only = null) {
    return memoGet(
        `${BASE}/costs/analysis/${buildQuery({
            month,
            account,
            account_level: accountLevel,
            account_group: accountGroup,
            group_by: groupBy,
            attribute_filters: serializeAttributeFilters(selectedItems),
            only,
        })}`
    );
}

function serializeAttributeFilters(selectedItems) {
    if (!selectedItems) return "";
    const cleaned = Object.entries(selectedItems).reduce((acc, [key, vals]) => {
        if (Array.isArray(vals) && vals.length > 0) acc[key] = vals;
        return acc;
    }, {});
    return Object.keys(cleaned).length > 0 ? JSON.stringify(cleaned) : "";
}

// KPIs de Estado de Resultados (tab KPIs, filtro central Ventas/Márgenes/
// Personas/Terceros). Devuelve las series de los 4 gráficos del grupo, con
// ambas variantes de cada toggle (YoY/Recurrentes/$) para alternar sin refetch.
export function getKpisResultados(initDate, endDate, group = "ventas", period = "month") {
    return memoGet(
        `${BASE}/kpis-resultados/${buildQuery({
            init_date: initDate,
            end_date: endDate,
            group,
            period,
        })}`
    );
}

// KPIs del tab Balance (`group`: liquidez | solvencia). Fase 1: cada grupo
// responde solo sus gráficos factibles sin "Cuenta X" (ver
// docs/DECISIONES.md §8).
export function getKpisBalance(initDate, endDate, group = "liquidez", period = "month") {
    return memoGet(
        `${BASE}/kpis-balance/${buildQuery({
            init_date: initDate,
            end_date: endDate,
            group,
            period,
        })}`
    );
}

// Tab Dashboard: tabla resumen del mes focal (`month` = "YYYY-MM") y series
// de los meses del rango en una sola respuesta. `range` = { initDate, endDate }
// en YYYY-MM-DD (el del tab, resuelto por `resolveDateRange`); la tabla es
// la del último mes del rango. `config` lleva el `signal` de takeLatest.
export function getDashboard(range, mode = "resultados", config = {}) {
    const query = buildQuery({ init_date: range.initDate, end_date: range.endDate, mode });
    return memoGet(`${BASE}/dashboard/${query}`, config);
}

// --- Export a xlsx (decisiones §34) -------------
// Asincrónico: el POST encola una corrida (202 {run_id}), `status` sirve el
// polling y `download` emite UNA vez la URL prefirmada de S3 (60 s).
// `endDate` null = el backend corta al último mes con datos.
export function startReportExport(endDate) {
    return request.post(`${BASE}/export/`, { end_date: endDate });
}

export function getReportExportStatus(runId) {
    return request.get(`${BASE}/export/status/${buildQuery({ run_id: runId })}`);
}

export function getReportExportDownload(runId) {
    return request.get(`${BASE}/export/download/${buildQuery({ run_id: runId })}`);
}
